/**
 * VpnSnippetDialog (F24.3) — diálogo que muestra el snippet RouterOS
 * listo para pegar + opción de descargar el .ovpn para PC.
 *
 * Tabs: "Mikrotik" (default) | "Cliente OpenVPN PC".
 * Botón "Copiar todo" en la pestaña Mikrotik.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Loader2, Terminal, Monitor } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { descargarOvpnConfig, useMikrotikSnippet, useOvpnCredencial } from "./api";

type Props = {
  credencialId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function VpnSnippetDialog({ credencialId, open, onOpenChange }: Props) {
  const { data: cred } = useOvpnCredencial(open ? credencialId : null);
  const { data: snippet, isLoading } = useMikrotikSnippet(open ? credencialId : null);
  const [tab, setTab] = useState<string>("mikrotik");

  function copyAll() {
    if (!snippet?.snippet) return;
    navigator.clipboard.writeText(snippet.snippet);
    toast.success("Snippet copiado al portapapeles");
  }

  async function downloadOvpn() {
    if (!cred) return;
    try {
      await descargarOvpnConfig(cred.id, `byteisp-${cred.ovpn_user.toLowerCase()}.ovpn`);
      toast.success(".ovpn descargado");
    } catch (e) {
      toast.error("No se pudo descargar", { description: (e as Error).message });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Terminal size={16} />
            VPN — {cred?.descripcion ?? "…"}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Usuario: <code className="font-mono">{cred?.ovpn_user ?? "…"}</code>
            {cred?.router_nombre && (
              <span className="ml-2">· Router: {cred.router_nombre}</span>
            )}
          </p>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mikrotik">
              <Terminal size={12} className="mr-1" />
              Mikrotik
            </TabsTrigger>
            <TabsTrigger value="pc">
              <Monitor size={12} className="mr-1" />
              Cliente PC
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mikrotik">
            <div className="flex flex-col gap-2">
              <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-700 dark:text-amber-400">
                ⚠ Este bloque contiene la password en claro. Solo se muestra
                mientras tengas el diálogo abierto. Si la pierdes, regenera
                desde el listado.
              </div>
              {isLoading ? (
                <div className="flex items-center justify-center py-6 text-muted-foreground">
                  <Loader2 size={16} className="animate-spin" />
                </div>
              ) : (
                <pre className="rounded-md border bg-muted/30 p-3 text-[10px] overflow-x-auto whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto">
                  {snippet?.snippet ?? ""}
                </pre>
              )}
              <div className="flex justify-between items-center pt-1">
                <p className="text-[10px] text-muted-foreground">
                  Copia TODO y pégalo en la terminal del Mikrotik (RouterOS 7+).
                  Sin pasos manuales — listo para usar.
                </p>
                <Button size="sm" onClick={copyAll} disabled={!snippet?.snippet}>
                  <Copy size={12} /> Copiar todo
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="pc">
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">
                Para clientes OpenVPN en Windows / Mac / Linux / iOS / Android.
                Descarga el archivo <code className="font-mono">.ovpn</code> y
                ábrelo con tu cliente OpenVPN. Te pedirá usuario y password —
                úsalos del modal donde se generó la credencial.
              </p>
              <div className="rounded border bg-muted/20 p-3 text-xs space-y-1">
                <div>
                  <span className="text-muted-foreground">Usuario:</span>{" "}
                  <code className="font-mono">{cred?.ovpn_user ?? "…"}</code>
                </div>
                <div className="text-muted-foreground">
                  Password: solo visible al generar/regenerar la credencial.
                  Si la perdiste, regenera desde el listado.
                </div>
              </div>
              <Button size="sm" onClick={downloadOvpn} className="w-fit">
                <Download size={12} /> Descargar .ovpn
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
